import { useEffect, useState } from "react";
import { ReactLenis, useLenis } from "lenis/react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { ShowreelProvider } from "@/components/site/Showreel";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Footer } from "@/components/site/Footer";
import { FestivalHeader } from "@/components/site/FestivalHeader";
import { FestivalHero, FestivalFilmsRow, FestivalPalmaresRow, FestivalJuryRow, FestivalEditionsStrip, editionVisual } from "@/components/site/FestivalShowcase";
import { filmAward } from "@/components/site/FestivalGraphics";
import { FestivalFilmModal } from "@/components/site/FestivalFilmModal";
import { FestivalJuryModal } from "@/components/site/FestivalJuryModal";
import { JoinDialog } from "@/components/site/JoinDialog";
import { galleryCover } from "@/lib/media";
import { resolveTheme, resolveSpacing } from "@/lib/festivalTheme";
import { resolveJuryMembers } from "@/lib/festivalJury";

const visibleFilms = (ed) =>
  (ed?.films || [])
    .filter((f) => f && !f.hidden && (f.title || f.poster))
    .map((f, i) => ({ ...f, _i: i }))
    .sort((a, b) => (a.order ?? a._i) - (b.order ?? b._i));

const sortedEditions = (editions) =>
  (editions || [])
    .filter((e) => e && !e.hidden)
    .map((e, i) => ({ ...e, _i: i }))
    .sort((a, b) => {
      const ao = a.order, bo = b.order;
      if (ao != null && bo != null && ao !== bo) return ao - bo;
      return (parseInt(b.year, 10) || 0) - (parseInt(a.year, 10) || 0);
    });

const buildPalmares = (edition) => {
  return visibleFilms(edition)
    .map((f) => ({ award: filmAward(f), film: f.title, poster: f.poster || galleryCover(f.media), posterFormat: f.posterFormat, detail: f.detail }))
    .filter((e) => e.award)
    .sort((a, b) => (b.award.principal ? 1 : 0) - (a.award.principal ? 1 : 0));
};

const FestivalBody = () => {
  const { content } = useContent();
  const location = useLocation();
  const navigate = useNavigate();
  const lenis = useLenis();
  const f = content.festival || {};
  const archive = content.festivalArchive || {};
  const sp = resolveSpacing(archive.spacing);
  const [film, setFilm] = useState(null);
  const [juryOpen, setJuryOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  // When arriving from the header menu on another page (Éditions / Films),
  // scroll to the requested section once its content has mounted.
  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return;
    let tries = 0;
    let timer;
    const tryScroll = () => {
      const el = document.getElementById(target);
      if (el) {
        if (lenis) lenis.scrollTo(el, { offset: -80 });
        else el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (tries++ < 40) {
        timer = setTimeout(tryScroll, 150);
      }
    };
    timer = setTimeout(tryScroll, 300);
    return () => clearTimeout(timer);
  }, [location.state, lenis]);

  const editions = sortedEditions(archive.editions);
  const yearFromEdition = (f.edition || "").match(/\d{4}/)?.[0];
  const current =
    editions.find((e) => e.current) ||
    editions.find((e) => String(e.year) === String(yearFromEdition)) ||
    editions[0] ||
    null;

  const year = current?.year || yearFromEdition || "2026";
  const films = visibleFilms(current);
  const palmares = buildPalmares(current);
  const jury = resolveJuryMembers(current, archive.juryLibrary).filter((m) => m && !m.hidden && (m.name || m.photo));
  const juryColorMode = current?.juryColorMode || archive.juryColorMode || "hover";
  const heroImage = editionVisual(current);

  const openApply = () => setJoinOpen(true);
  const themeVars = resolveTheme(archive.theme, current);

  // Ouvre le formulaire de candidature si on arrive via « Participer » (?apply=1),
  // puis nettoie l'URL pour éviter une réouverture au rafraîchissement / retour arrière.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("apply") === "1") {
      setJoinOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  return (
    <div className="App festival-universe min-h-screen flex flex-col" style={{ ...themeVars, backgroundColor: "var(--fx-bg)", color: "var(--fx-text)" }}>
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <FestivalHeader brand={content.brand || {}} socials={content.contact?.socials || []} onApply={openApply} />

      <main className="flex-1" data-testid="festival-page">
        <FestivalHero festival={f} edition={current} heroImage={heroImage} onApply={openApply} />

        <div className="max-w-[1500px] mx-auto px-5 md:px-10" style={{ paddingTop: sp.pageTop, paddingBottom: sp.pageBottom }} data-testid="festival-sections">
          <div style={{ marginTop: sp.films }}><FestivalFilmsRow films={films} year={year} edition={current} onFilm={setFilm} /></div>
          <div style={{ marginTop: sp.palmares }}><FestivalPalmaresRow entries={palmares} year={year} edition={current} /></div>
          <div style={{ marginTop: sp.jury }}><FestivalJuryRow members={jury} year={year} colorMode={juryColorMode} onJury={() => setJuryOpen(true)} /></div>
          <div style={{ marginTop: sp.editions }}><FestivalEditionsStrip editions={editions} currentId={current?.id || current?.year} /></div>
        </div>
      </main>

      <Footer />

      <FestivalFilmModal film={film?.film} editionYear={film?.year} onClose={() => setFilm(null)} />
      <FestivalJuryModal edition={juryOpen ? { ...current, jury } : null} colorMode={juryColorMode} onClose={() => setJuryOpen(false)} />
      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} type="festival" refId="festival"
        refTitle={`Karams Festival — Édition ${year}`} roles={f.joinRoles || []}
        notice={f.participation?.notice} documents={f.participation?.documents || []}
        heading="Soumettre ma candidature" intro="Candidatez, devenez bénévole ou partenaire du Karams Festival." />
    </div>
  );
};

export default function FestivalPage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <ShowreelProvider>
          <Analytics />
          <FestivalBody />
        </ShowreelProvider>
      </ReactLenis>
    </ContentProvider>
  );
}
